import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProcedureRevisionsForPart } from '../../../redux/procedureRevisionSlice';
import {
  getAllTrainingLogs,
  getTrainingLogsForProcedureRevision,
} from '../../../redux/trainingLogSlice';
import {
  fetchUsers,
  fetchApprovedUsers,
  fetchReleasedUsers,
  fetchSubmittedUsers,
} from '../../../redux/userSlice';
import { getBom } from '../../../redux/partSlice';
import useCurrentRevisionData from './useCurrentRevisionData';
import { isLocked } from '../Utils/utils.js';
import { dedupeTrainingLogsLatestPerTraineePerRevision } from '../../../util/trainingLogStatus';

/**
 * Every procedure revision id for this part (any status). Used only when `partId` is unavailable
 * and we must fall back to per-revision GET (see `loadTrainingLogsForDocument`).
 */
function collectProcedureRevisionIdsForTrainingLogFetch(revs, routeRevisionId) {
  const fromList = Array.isArray(revs)
    ? revs
        .map((rev) => rev?.procedure_revision_id ?? rev?.procedure_revision_ref)
        .filter(Boolean)
    : [];
  return Array.from(
    new Set([...fromList, routeRevisionId].filter(Boolean)),
  );
}

const TRAINING_LOG_PART_FETCH_LIMIT = 10000;

/**
 * Prefer GET training-log/get-all?part_ref=… so **all** revision rows load even when
 * `getProcedureRevisionsForPart` omits superseded or amended parents (e.g. A1 missing after A1a).
 * Per-revision queries would drop history for those ids.
 */
function loadTrainingLogsByPartRef(dispatch, partId) {
  return dispatch(
    getAllTrainingLogs({
      limit: TRAINING_LOG_PART_FETCH_LIMIT,
      offset: 0,
      part_ref: String(partId).trim(),
    }),
  )
    .unwrap()
    .then((payload) => {
      const raw = payload?.results ?? payload?.data;
      const list = Array.isArray(raw) ? raw : [];
      return list;
    });
}

function loadTrainingLogsPerRevisionMerge(dispatch, partId, procedureRevisionIds) {
  const ids = Array.isArray(procedureRevisionIds)
    ? procedureRevisionIds.filter(Boolean).map(String)
    : [];
  if (!ids.length) {
    return Promise.resolve([]);
  }

  return Promise.all(
    ids.map((id) =>
      dispatch(
        getTrainingLogsForProcedureRevision({
          procedureRevisionId: id,
          limit: TRAINING_LOG_PART_FETCH_LIMIT,
          offset: 0,
          ...(partId ? { part_ref: String(partId).trim() } : {}),
        }),
      )
        .unwrap()
        .then((payload) => {
          const raw = payload?.results ?? payload?.data;
          const list = Array.isArray(raw) ? raw : [];
          return list.map((r) => ({
            ...r,
            procedure_revision_ref:
              r.procedure_revision_ref ??
              r.procedure_revision_id ??
              id,
          }));
        })
        .catch(() => []),
    ),
  ).then((chunks) => chunks.flat());
}

/**
 * Custom hook to fetch and manage all document-related data
 * Centralizes data fetching logic for document pages
 * 
 * @param {string} partId - The part ID
 * @param {string} revisionId - The revision ID
 * @returns {Object} All document-related data from Redux store with loading states
 */
const useDocumentData = (partId, revisionId, magicUrl = false,otherParams={}) => {
  const dispatch = useDispatch();

  // Loading states for different sections
  const [loading, setLoading] = useState({
    revisionData: true,
    trainingLogs: true,
    bomTable: true,
    users: true,
  });

  const { revisionData: reduxRevisionData } = useSelector((state) => state.procedureRevision);
  const revisionData = otherParams.fromAuditTrail === true && otherParams.present_revisions
    ? otherParams.present_revisions
    : reduxRevisionData;

  /** Rows from GET training-log/get-all?part_ref=… (full part history; see `loadTrainingLogsByPartRef`). */
  const [trainingLogs, setTrainingLogs] = useState([]);
  
  const {
    users: allUsersForTraineePicker,
    submittedUsers: users,
    approvedUsers,
    releasedUsers,
  } = useSelector((state) => state.users);

  const { rule } = useSelector((state) => state.procedureTemplateRule);
  const { bomTable } = useSelector((state) => state.parts);

  const loadTrainingLogsForDocument = useCallback(
    (procedureRevisionIdsFallback) => {
      const ids = Array.isArray(procedureRevisionIdsFallback)
        ? procedureRevisionIdsFallback.filter(Boolean).map(String)
        : [];

      if (!partId) {
        if (!ids.length) {
          setTrainingLogs([]);
          return Promise.resolve();
        }
        return loadTrainingLogsPerRevisionMerge(dispatch, null, ids)
          .then((merged) => {
            setTrainingLogs(
              dedupeTrainingLogsLatestPerTraineePerRevision(merged),
            );
          })
          .catch(() => {
            setTrainingLogs([]);
          });
      }

      /**
       * Merge part-scoped GET with per-revision GET. Some deployments return only stub rows on one
       * path (e.g. `training_log_id` === `procedure_revision_ref`) or omit history unless both are queried.
       */
      return Promise.all([
        loadTrainingLogsByPartRef(dispatch, partId).catch(() => []),
        ids.length
          ? loadTrainingLogsPerRevisionMerge(dispatch, partId, ids).catch(
              () => [],
            )
          : Promise.resolve([]),
      ])
        .then(([fromPart, fromRevs]) => {
          const merged = [...fromPart, ...fromRevs];
          setTrainingLogs(
            dedupeTrainingLogsLatestPerTraineePerRevision(merged),
          );
        })
        .catch(() => {
          setTrainingLogs([]);
        });
    },
    [dispatch, partId],
  );

  // Fetch all required data on mount or when dependencies change
  useEffect(() => {
    if (partId && revisionId) {
      // Reset loading states
      setLoading({
        revisionData: true,
        trainingLogs: true,
        bomTable: true,
        users: true,
      });

      if(!otherParams?.fromAuditTrail){
        dispatch(getProcedureRevisionsForPart({ partId }))
          .unwrap()
          .then((payload) => {
            const revs = Array.isArray(payload?.results) ? payload.results : [];
            const idsToLoad = collectProcedureRevisionIdsForTrainingLogFetch(
              revs,
              revisionId,
            );
            return loadTrainingLogsForDocument(
              idsToLoad.length ? idsToLoad : [revisionId],
            );
          })
          .finally(() => {
            setLoading((prev) => ({ ...prev, revisionData: false, trainingLogs: false }));
          });
      }else{
        setLoading((prev) => ({ ...prev, revisionData: false, trainingLogs: false }));
      }
      
      // Fetch user lists
      Promise.all([
        dispatch(fetchUsers({ limit: 1000, offset: 0 })),
        dispatch(fetchSubmittedUsers({ limit: 100, offset: 0 })),
        dispatch(fetchReleasedUsers({ limit: 100, offset: 0 })),
        dispatch(fetchApprovedUsers({ limit: 100, offset: 0 })),
      ]).then(() => {
        setLoading((prev) => ({ ...prev, users: false }));
      });
    }
  }, [dispatch, partId, revisionId, loadTrainingLogsForDocument]);

  // Determine if locked to conditionally fetch BOM
  // We need current revision data for this
  const { currentStatus } = useCurrentRevisionData(revisionData, revisionId);
  const isLock = isLocked(currentStatus);

  // Fetch BOM table only if NOT locked
  useEffect(() => {
    if (partId && revisionId && revisionData && revisionData.length > 0) {
      if (!isLock) {
         // Not locked: Fetch from API
         dispatch(getBom({ partId, revisionId, withoutToken: magicUrl })).then(() => {
           setLoading((prev) => ({ ...prev, bomTable: false }));
         });
      } else {
         // Locked: Do not fetch from API, simply mark as loaded
         // content will be derived from valid nodes in Document.js
         setLoading((prev) => ({ ...prev, bomTable: false }));
      }
    }
  }, [dispatch, partId, revisionId, isLock, revisionData]);

  // Function to refresh revision data (useful for callbacks)
  const refreshRevisionData = () => {
    if (!partId) return;
    setLoading((prev) => ({ ...prev, revisionData: true, trainingLogs: true }));
    dispatch(getProcedureRevisionsForPart({ partId }))
      .unwrap()
      .then((payload) => {
        const revs = Array.isArray(payload?.results) ? payload.results : [];
        const idsToLoad = collectProcedureRevisionIdsForTrainingLogFetch(
          revs,
          revisionId,
        );
        return loadTrainingLogsForDocument(
          idsToLoad.length ? idsToLoad : [revisionId],
        );
      })
      .finally(() => {
        setLoading((prev) => ({
          ...prev,
          revisionData: false,
          trainingLogs: false,
        }));
      });
  };

  const refreshBomTable = useCallback(() => {
    if (!partId || !revisionId || !revisionData?.length) return;
    if (!isLocked(currentStatus)) {
      dispatch(getBom({ partId, revisionId, withoutToken: magicUrl }));
    }
  }, [dispatch, partId, revisionId, revisionData, currentStatus, magicUrl]);

  return {
    // Data
    revisionData,
    trainingLogs,
    users,
    allUsersForTraineePicker,
    approvedUsers,
    releasedUsers,
    rule,
    bomTable,
    
    // Loading states
    loading,
    
    // Actions
    refreshRevisionData,
    refreshBomTable,
  };
};

export default useDocumentData;

